import { useEffect, useState, useCallback, useRef } from "react";
import { Play, X, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const TESTIMONIALS = [
  {
    tag: "Authority Growth",
    label: "#1 Business Expert",
    thumb:
      "https://i.vimeocdn.com/video/2143998277-cea5740ab367eed3ed47c54ada2b085f3f48645dd3eec0bf1f5ba8491e381446-d?mw=900&q=85",
    // TEMP stand-in until the real Vimeo/YouTube link is provided
    videoUrl: "/videos/sample-a.webm",
    // videoUrl: "https://player.vimeo.com/video/XXXXXXXXX?h=xxxxx"  // <-- paste when ready
  },
  {
    tag: "Content Performance",
    label: "#1 Health Expert",
    thumb:
      "https://i.vimeocdn.com/video/2143998128-7f1ef1d2733bff9658d304d54267e429cf3529a827cbd580aa57a4eccd0c5444-d?mw=900&q=85",
    videoUrl: "/videos/sample-a.webm",
  },
  {
    tag: "Lead Generation",
    label: "Skin Expert",
    thumb:
      "https://i.vimeocdn.com/video/2160313519-bd766f6178bc6a0328f260bf31f96082e70ac5b341fd25e7e7440ae268ab34ea-d?mw=900&q=85",
    videoUrl: "/videos/sample-a.webm",
  },
  {
    tag: "Business Growth",
    label: "Yoga Expert",
    thumb:
      "https://i.vimeocdn.com/video/2160313509-0d0c44e93c8ec6306eb1f43c99009fa36bcf846fcec7171e64e6b077f60cbcac-d?mw=900&q=85",
    videoUrl: "/videos/sample-a.webm",
  },
  {
    tag: "Implementation",
    label: "Performance Expert",
    thumb:
      "https://i.vimeocdn.com/video/2160313455-753796cc404ad9326fb3011e278b5885f4a30922e722ab0ef1f732bccdde7d0d-d?mw=900&q=85",
    videoUrl: "/videos/sample-b.webm",
  },
  {
    tag: "Business Growth",
    label: "Corporate Health Expert",
    thumb:
      "https://i.vimeocdn.com/video/2166611629-7cec5d417a0700306a902af20a81eefa8ff85367d84d6aca386a0e467bb0f67a-d?mw=900&q=85",
    videoUrl: "/videos/sample-b.webm",
  },
  {
    tag: "Lead Generation",
    label: "PCOD Expert",
    thumb:
      "https://i.vimeocdn.com/video/2166611562-36621957e5c5f632a146a9fbe45ce47e353f03d5df9da2567a1fb2767e1e2664-d?mw=900&q=85",
    videoUrl: "/videos/sample-b.webm",
  },
  {
    tag: "Lead Generation",
    label: "Life Coach Expert",
    thumb:
      "https://i.vimeocdn.com/video/2166610168-a0ec9760dc5c30ba7696040d78b34758259aef713674548544c91395feaf3be2-d?mw=900&q=85",
    videoUrl: "/videos/sample-b.webm",
  },
];

// Normalise a user-provided video URL (YouTube or Vimeo) → embed URL
const toEmbedUrl = (url) => {
  if (!url) return null;
  // Already an embed URL
  if (url.includes("player.vimeo.com") || url.includes("youtube.com/embed")) {
    return url;
  }
  // youtu.be / youtube.com/watch?v=
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  // vimeo.com/{id} or vimeo.com/{id}/{hash}
  const vm = url.match(/vimeo\.com\/(\d+)(?:\/([\w]+))?/);
  if (vm) {
    const hash = vm[2] ? `?h=${vm[2]}&autoplay=1` : "?autoplay=1";
    return `https://player.vimeo.com/video/${vm[1]}${hash}`;
  }
  return url;
};

const Card = ({ t, onOpen, index }) => (
  <button
    type="button"
    data-testid={`testimonial-card-${index}`}
    onClick={() => onOpen(t)}
    aria-label={`Play testimonial — ${t.label}`}
    className="group relative shrink-0 w-[210px] md:w-[240px] aspect-[9/16] rounded-[20px] overflow-hidden border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-[#b98d34]/40 cursor-pointer transition-all duration-500 hover:-translate-y-1"
    style={{
      boxShadow:
        "0 24px 50px -22px rgba(10,10,10,0.35), 0 1px 0 rgba(255,255,255,0.7) inset",
    }}
  >
    <img
      src={t.thumb}
      alt={`${t.label} — ${t.tag}`}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
      draggable={false}
    />
    {/* Cinematic top-to-bottom gradient for readable text on any thumb */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/85" />
    {/* Inner gold ring that appears on hover */}
    <div
      aria-hidden
      className="absolute inset-0 rounded-[20px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
      style={{
        boxShadow: "0 0 0 1px rgba(212,175,55,0.6) inset, 0 0 40px -6px rgba(212,175,55,0.35)",
      }}
    />

    {/* Category tag — top-left */}
    <span
      className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9.5px] tracking-[0.16em] uppercase text-white pointer-events-none"
      style={{
        background: "rgba(10,10,10,0.55)",
        border: "1px solid rgba(255,255,255,0.12)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <span className="w-1 h-1 rounded-full" style={{ background: "#e6c96b" }} />
      {t.tag}
    </span>

    {/* Play button */}
    <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <span className="relative flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/15 border border-white/30 backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
        <span
          aria-hidden
          className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: "radial-gradient(closest-side, rgba(212,175,55,0.35), transparent 70%)" }}
        />
        <Play className="relative w-4 h-4 md:w-5 md:h-5 text-white fill-white ml-0.5" strokeWidth={0} />
      </span>
    </span>

    {/* Bottom label block */}
    <div className="absolute inset-x-0 bottom-0 p-4 pointer-events-none">
      <div className="flex items-end justify-between gap-2">
        <div>
          <div className="text-white text-[15px] md:text-[17px] font-semibold tracking-tight leading-tight drop-shadow-lg">
            {t.label}
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-white/55 text-[9.5px] tracking-[0.2em] uppercase">
            <span className="w-3 h-px bg-white/40" />
            Reel · Case study
          </div>
        </div>
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background: "rgba(212,175,55,0.95)",
            color: "#0a0a0a",
          }}
        >
          →
        </span>
      </div>
    </div>
  </button>
);

// A single horizontally-scrollable row with left/right arrow controls.
export default function Testimonials() {
  const [active, setActive] = useState(null);
  const [activeIdx, setActiveIdx] = useState(2);
  const swipeStartX = useRef(null);
  const didSwipe = useRef(false);

  const close = useCallback(() => setActive(null), []);
  const prevCard = useCallback(
    () => setActiveIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length),
    []
  );
  const nextCard = useCallback(
    () => setActiveIdx((i) => (i + 1) % TESTIMONIALS.length),
    []
  );

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active, close]);

  return (
    <section
      data-testid="testimonials-section"
      className="relative py-16 md:py-24 border-t border-[#e5e2d8] overflow-hidden"
    >
      <div className="mx-auto max-w-[1200px] px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10 md:mb-14 items-end">
          <div className="lg:col-span-8">
            <span className="eyebrow" data-testid="testimonials-eyebrow">
              <span className="eyebrow-dot" />
              Voices from the room
            </span>
            <h2 className="mt-6 font-semibold tracking-tight text-3xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              <span className="text-gradient">The experts who chose</span>{" "}
              <span className="text-gradient-dim">to build differently.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 space-y-5">
            <p className="text-[#0a0a0a]/60 text-[15px] leading-relaxed">
              See what&apos;s possible when the system does the heavy lifting —
              real founders, real outcomes, in their own words.
            </p>
            <div className="flex items-center gap-3">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11.5px] tracking-wider text-[#0a0a0a]"
                style={{
                  border: "1px solid rgba(212,175,55,0.35)",
                  background: "rgba(212,175,55,0.08)",
                }}
              >
                <span className="relative flex w-2 h-2">
                  <span className="absolute inset-0 rounded-full bg-[#b98d34] animate-ping opacity-60" />
                  <span className="relative inline-flex rounded-full w-2 h-2 bg-[#b98d34]" />
                </span>
                LIVE · 8 CASES ON DECK
              </div>
              <div className="text-[#0a0a0a]/45 text-[11.5px] tracking-widest uppercase">
                Reel format
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Coverflow — static until swiped; swipe/drag left or right to browse */}
      <div
        className="relative"
        style={{ touchAction: "pan-y" }}
        onPointerDown={(e) => {
          swipeStartX.current = e.clientX;
          didSwipe.current = false;
        }}
        onPointerUp={(e) => {
          if (swipeStartX.current === null) return;
          const dx = e.clientX - swipeStartX.current;
          if (Math.abs(dx) > 50) {
            didSwipe.current = true;
            if (dx < 0) nextCard();
            else prevCard();
          }
          swipeStartX.current = null;
        }}
      >
        <div className="relative mx-auto max-w-[1200px] h-[560px] md:h-[640px] flex items-center justify-center px-4 md:px-8">
          {TESTIMONIALS.map((t, i) => {
            const offset = i - activeIdx;
            const abs = Math.abs(offset);
            if (abs > 3) return null;
            const scale = abs === 0 ? 1 : abs === 1 ? 0.78 : abs === 2 ? 0.62 : 0.5;
            const opacity = abs === 0 ? 1 : abs === 1 ? 0.55 : abs === 2 ? 0.28 : 0.12;
            const blur = abs === 0 ? 0 : abs === 1 ? 6 : abs === 2 ? 12 : 18;
            const xStep = 220;
            const x = offset * xStep;
            const z = 100 - abs * 10;
            return (
              <motion.button
                key={i}
                type="button"
                data-testid={`testimonial-card-${i}`}
                onClick={() => {
                  if (didSwipe.current) {
                    didSwipe.current = false;
                    return;
                  }
                  abs === 0 ? setActive(t) : setActiveIdx(i);
                }}
                animate={{ x, scale, opacity, filter: `blur(${blur}px)` }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  zIndex: z,
                  pointerEvents: abs > 2 ? "none" : "auto",
                  boxShadow: abs === 0
                    ? "0 40px 80px -20px rgba(10,10,10,0.5), 0 0 0 1px rgba(212,175,55,0.4) inset, 0 0 60px -10px rgba(212,175,55,0.35)"
                    : "0 30px 60px -30px rgba(10,10,10,0.4)",
                }}
                className="absolute w-[300px] md:w-[360px] aspect-[9/16] rounded-[24px] overflow-hidden border border-black/10 bg-white focus:outline-none cursor-pointer"
              >
                <img
                  src={t.thumb}
                  alt={`${t.label} — ${t.tag}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/85" />

                {abs === 0 && (
                  <>
                    <span
                      className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] tracking-[0.16em] uppercase text-white"
                      style={{
                        background: "rgba(10,10,10,0.55)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        backdropFilter: "blur(8px)",
                        WebkitBackdropFilter: "blur(8px)",
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#e6c96b" }} />
                      {t.tag}
                    </span>

                    <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="relative flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/20 border border-white/40 backdrop-blur-md">
                        <Play className="relative w-6 h-6 md:w-7 md:h-7 text-white fill-white ml-0.5" strokeWidth={0} />
                      </span>
                    </span>

                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <div className="text-white text-[20px] md:text-[24px] font-semibold tracking-tight leading-tight drop-shadow-lg">
                        {t.label}
                      </div>
                      <div className="mt-1.5 flex items-center gap-1.5 text-white/60 text-[10.5px] tracking-[0.2em] uppercase">
                        <span className="w-3 h-px bg-white/40" />
                        Reel · Case study
                      </div>
                    </div>
                  </>
                )}
              </motion.button>
            );
          })}
        </div>

        {/* Prev / Next controls */}
        <button
          type="button"
          aria-label="Previous testimonial"
          data-testid="testimonial-prev"
          onClick={() => setActiveIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
          className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full flex items-center justify-center bg-white border border-black/10 shadow-[0_14px_30px_-8px_rgba(10,10,10,0.20)] hover:scale-110 hover:border-[#b98d34] transition-all"
        >
          <ChevronLeft className="w-5 h-5 text-[#0a0a0a]" strokeWidth={2} />
        </button>
        <button
          type="button"
          aria-label="Next testimonial"
          data-testid="testimonial-next"
          onClick={() => setActiveIdx((i) => (i + 1) % TESTIMONIALS.length)}
          className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full flex items-center justify-center bg-white border border-black/10 shadow-[0_14px_30px_-8px_rgba(10,10,10,0.20)] hover:scale-110 hover:border-[#b98d34] transition-all"
        >
          <ChevronRight className="w-5 h-5 text-[#0a0a0a]" strokeWidth={2} />
        </button>

        {/* Dot indicator */}
        <div className="flex items-center justify-center gap-1.5 mt-2">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIdx(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === activeIdx ? "w-8 bg-[#b98d34]" : "w-1.5 bg-[#0a0a0a]/20 hover:bg-[#0a0a0a]/40"
              }`}
            />
          ))}
        </div>
      </div>

      <p className="mt-10 md:mt-14 text-center text-[#0a0a0a]/40 text-[12px] px-5 max-w-3xl mx-auto">
        Testimonials reflect individual experiences. References to revenue,
        followers or outcomes are specific to those individuals and are not a
        guarantee of your results.
      </p>

      {/* Video Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="lightbox"
            data-testid="testimonial-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8"
            style={{
              background: "rgba(0,0,0,0.86)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.65, 0, 0.35, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-[420px] aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0a]"
              style={{ boxShadow: "0 60px 120px -30px rgba(0,0,0,0.9)" }}
            >
              <button
                onClick={close}
                data-testid="lightbox-close"
                aria-label="Close video"
                className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/60 border border-white/15 text-white hover:bg-black/80 transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </button>

              {/\.(mp4|webm|mov)(\?|$)/i.test(active.videoUrl || "") ? (
                <video
                  key={active.label}
                  src={active.videoUrl}
                  controls
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="lightbox-video"
                />
              ) : toEmbedUrl(active.videoUrl) ? (
                <iframe
                  key={active.label}
                  src={toEmbedUrl(active.videoUrl)}
                  title={active.label}
                  allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="lightbox-iframe"
                />
              ) : (
                <div className="w-full h-full relative flex flex-col items-center justify-center text-center px-6">
                  <img
                    src={active.thumb}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-30"
                    draggable={false}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black/90" />
                  <div className="relative">
                    <span
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] text-white/80"
                      style={{
                        background: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.14)",
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-white" />
                      {active.tag}
                    </span>
                    <div className="mt-6 text-white text-xl font-medium">
                      {active.label}
                    </div>
                    <p className="mt-4 text-white/70 text-[13.5px] leading-relaxed max-w-[280px] mx-auto">
                      Video source not yet connected. Send the Vimeo / YouTube
                      link for this testimonial and it will play here.
                    </p>
                    <button
                      onClick={close}
                      className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] text-white border border-white/25 hover:border-white/50 hover:bg-white/10 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
